<template>
  <StatCard
    title="Priority"
    subtitle="Completed in this period, open and overdue as of now"
    :loading="loading"
    :empty="isEmpty"
    empty-title="No tasks to break down"
  >
    <div ref="chartEl" class="w-full h-64"></div>
  </StatCard>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import StatCard from './StatCard.vue'

const props = defineProps({
  data: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const chartEl = ref(null)
let chart = null

// Critical at the top: the question this chart answers is whether the
// important work is the work getting done, so it should be read downwards.
const ordered = computed(() => [...props.data].reverse())
const isEmpty = computed(() =>
  !props.loading && props.data.every(d => d.completed + d.open + d.overdue === 0))

function render() {
  if (!chartEl.value || isEmpty.value) return
  // Highcharts arrives from a CDN via layouts/_highcharts.html.erb. If that is
  // blocked or still in flight, degrade to an empty chart area rather than
  // throwing and taking the rest of the page down with it.
  if (typeof Highcharts === 'undefined') return
  if (chart) { chart.destroy(); chart = null }

  chart = Highcharts.chart(chartEl.value, {
    chart: { type: 'bar', height: 256, spacing: [16, 16, 8, 8] },
    xAxis: {
      categories: ordered.value.map(d => d.priority.charAt(0).toUpperCase() + d.priority.slice(1)),
      lineWidth: 0, tickLength: 0,
    },
    yAxis: { min: 0, allowDecimals: false, title: { text: null }, stackLabels: { enabled: false } },
    tooltip: { shared: true },
    legend: { align: 'center', verticalAlign: 'bottom' },
    plotOptions: { series: { stacking: 'normal', borderWidth: 0, pointPadding: 0.12, groupPadding: 0.12 } },
    series: [
      { name: 'Completed', data: ordered.value.map(d => d.completed), color: '#10b981' },
      { name: 'Open', data: ordered.value.map(d => d.open), color: '#94a3b8' },
      { name: 'Overdue', data: ordered.value.map(d => d.overdue), color: '#ef4444' },
    ],
  })
}

watch(() => props.data, () => nextTick(render), { deep: true })
onMounted(() => { if (!isEmpty.value) nextTick(render) })
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
